import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { P04_STYLE } from "@/components/P04Panel";

const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : Array.isArray(v) ? v.join(" × ") : String(v));
const St = ({ s }) => <Badge className={`text-[9px] ${P04_STYLE[s] || "bg-zinc-100"}`}>{s || "—"}</Badge>;

const Item = ({ neutralId, item, active, onResolved }) => {
  const [action, setAction] = useState(null);
  const [value, setValue] = useState("");
  const [motivation, setMotivation] = useState("");
  const [busy, setBusy] = useState(false);
  const bb = item.bbox_page_pt;
  const cropUrl = bb && bb.length === 4
    ? `${api.defaults.baseURL}/p05/cases/${neutralId}/crop-image/${item.page}?x0=${bb[0]}&y0=${bb[1]}&x1=${bb[2]}&y1=${bb[3]}` : null;
  const send = async (act) => {
    setBusy(true);
    try {
      await api.post(`/p05/cases/${neutralId}/review/items/${item.item_id}`, {
        action: act, value: act === "modify" ? value : (act === "confirm" ? (item.ai_value ?? item.parser_value) : null), motivation,
      });
      toast.success("Azione registrata");
      onResolved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(false);
  };
  return (
    <div className="border border-border p-2 space-y-1" data-testid={`p05-review-item-${item.kind}-${item.key}`}>
      <div className="flex items-center gap-2 flex-wrap text-[11px]">
        <St s={item.state} /><b>{item.kind}: {item.key}</b>
        {item.resolution && <Badge className="text-[9px] bg-blue-100 text-blue-800">{item.resolution.action}</Badge>}
      </div>
      <div className="grid md:grid-cols-2 gap-2">
        <div className="text-[11px] space-y-0.5">
          <div>Parser: <b>{fmt(item.parser_value)}</b> · IA: <b>{fmt(item.ai_value)}</b></div>
          {item.ai_source_text && <div className="text-muted-foreground">«{item.ai_source_text}»</div>}
          <div className="text-amber-800">{item.motivation}</div>
          <div className="text-rose-800">Impatto: {item.quote_impact || "—"}</div>
        </div>
        {cropUrl && <img src={cropUrl} alt="crop" className="border border-border max-h-32 object-contain justify-self-start" />}
      </div>
      {active && !item.resolution && (
        <div className="space-y-1">
          <div className="flex gap-1 flex-wrap">
            <Button size="sm" className="h-6 text-[11px]" disabled={busy} onClick={() => send("confirm")} data-testid={`p05-confirm-${item.item_id}`}>Conferma</Button>
            <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => setAction(action === "modify" ? null : "modify")}>Modifica</Button>
            <Button size="sm" variant="outline" className="h-6 text-[11px] text-red-700" onClick={() => setAction(action === "reject" ? null : "reject")}>Rifiuta</Button>
            <Button size="sm" variant="outline" className="h-6 text-[11px]" disabled={busy} onClick={() => send("not_present")} data-testid={`p05-notpresent-${item.item_id}`}>Dato non presente</Button>
          </div>
          {action && (
            <div className="flex gap-1">
              {action === "modify" && <Input className="h-6 text-[11px] max-w-[140px]" placeholder="valore" value={value} onChange={(e) => setValue(e.target.value)} />}
              <Textarea className="text-[11px] min-h-[26px]" placeholder="motivazione (obbligatoria)" value={motivation} onChange={(e) => setMotivation(e.target.value)} />
              <Button size="sm" className="h-6 text-[11px]" disabled={busy || !motivation || (action === "modify" && !value)} onClick={() => send(action)}>Invia</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const P05ReviewPanel = ({ neutralId }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/p05/cases/${neutralId}/review`).then((r) => setData(r.data))
      .catch((e) => setData({ missing: true, reason: e?.response?.data?.detail }));
  }, [neutralId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-xs">Caricamento…</p>;
  if (data.missing) return <p className="text-xs text-amber-800" data-testid="p05-review-missing">{data.reason}</p>;

  const started = Boolean(data.review?.started_at);
  const completed = Boolean(data.review?.completed_at);
  const start = async () => {
    setBusy(true);
    try { await api.post(`/p05/cases/${neutralId}/review/start`); toast.success("Cronometro avviato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(false);
  };
  const complete = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/p05/cases/${neutralId}/review/complete`);
      toast.success(`Revisione congelata (${r.data.stats.review_seconds}s)`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(false);
  };
  const gate = data.reading_gate || {};
  return (
    <div className="space-y-2" data-testid="p05-review-panel">
      <div className="flex items-center gap-2 flex-wrap text-[11px]">
        <St s={gate.status} />
        <span>chiamate: {data.meta?.n_calls} · costo ${data.meta?.cost_usd} · {data.meta?.elapsed_s}s</span>
        {!started && <Button size="sm" className="h-6 text-[11px]" disabled={busy} onClick={start} data-testid="p05-review-start">Avvia revisione (cronometrata)</Button>}
        {started && !completed && <Button size="sm" variant="destructive" className="h-6 text-[11px]" disabled={busy} onClick={complete} data-testid="p05-review-complete">Concludi e congela HUMAN_REVIEWED</Button>}
        {completed && <Badge className="bg-emerald-100 text-emerald-800">revisione congelata: {data.review?.stats?.review_seconds}s</Badge>}
      </div>
      <p className="text-[10px] text-amber-800">{data.blind_note}</p>
      {(data.review_items || []).map((it) => (
        <Item key={it.item_id} neutralId={neutralId} item={it} active={started && !completed} onResolved={load} />
      ))}
      {(data.review_items || []).length === 0 && <p className="text-xs text-emerald-800">Nessuna voce in coda.</p>}
    </div>
  );
};
